import type RTKClient from '@cloudflare/realtimekit';

export type AudioPreset = 'voice' | 'music' | 'broadcast';
export type ShareMode = 'detail' | 'motion';

export interface AudioCfg {
  enableStereo: boolean;
  enableHighBitrate: boolean;
  echoCancellation: boolean;
  noiseSupression: boolean;
  autoGainControl: boolean;
  /** Default Opus bitrate (bps) applied after (re)join for this preset. */
  defaultBitrate: number;
}

// Stereo, no voice processing. The only supported way to get the SDK to
// negotiate stereo Opus at its top `maxaveragebitrate` (128 kbps) on the send
// transport — see AUDIO_CONFIG.music / .broadcast below. enableStereo +
// enableHighBitrate are SESSION-GLOBAL (one send transport for mic AND
// screenshare audio), so this same flag governs shared tab/system audio.
const STEREO_FULLBAND = {
  enableStereo: true,
  enableHighBitrate: true,
  echoCancellation: false,
  noiseSupression: false,
  autoGainControl: false,
  defaultBitrate: 128_000,
} as const;

export const AUDIO_CONFIG: Record<AudioPreset, AudioCfg> = {
  voice: {
    enableStereo: false,
    enableHighBitrate: true,
    echoCancellation: true,
    noiseSupression: true,
    autoGainControl: true,
    defaultBitrate: 64_000,
  },
  // Music: hi-fi stereo for a mic carrying music/instruments.
  music: { ...STEREO_FULLBAND },
  // Broadcast: same stereo-fullband capture, framed for sharing a browser
  // tab's audio at full fidelity. Because enableStereo is on, the shared-audio
  // producer negotiates stereo Opus (vs Voice's mono, band-limited 64k) — which
  // is what actually makes captured tab audio sound full instead of "tinny".
  // Pairs with installCleanScreenshareAudio() (DSP-off, 2-channel capture) and
  // the selectable shared-audio bitrate (BROADCAST_BITRATE_OPTIONS). Defaults to
  // 256k — transparent stereo without wasting uplink on sources that rarely
  // exceed it; 512k is available for the most demanding material.
  broadcast: { ...STEREO_FULLBAND, defaultBitrate: 256_000 },
};

/** Selectable Broadcast-preset bitrates (kbps) — shown only in Broadcast. */
export const BROADCAST_BITRATE_OPTIONS = [128, 256, 512] as const;

/** Selectable Opus bitrates (kbps). 64 is the default. */
export const BITRATE_OPTIONS = [16, 32, 64, 96, 128] as const;

interface ShareModeCfg {
  contentHint: 'detail' | 'motion';
  constraints: { width: { ideal: number }; height: { ideal: number }; frameRate: { ideal: number } };
  maxBitrate: number;
}

export const SHARE_MODE_CONFIG: Record<ShareMode, ShareModeCfg> = {
  // Crisp text/code: high resolution, low frame rate.
  detail: {
    contentHint: 'detail',
    constraints: { width: { ideal: 2560 }, height: { ideal: 1440 }, frameRate: { ideal: 5 } },
    maxBitrate: 1_500_000,
  },
  // Smooth video/gameplay: cap 720p, 30 fps, higher bitrate.
  motion: {
    contentHint: 'motion',
    constraints: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
    maxBitrate: 2_500_000,
  },
};

let displayAudioPatched = false;
/**
 * Capture screen/tab audio WITHOUT speech processing. Chrome applies
 * echoCancellation/noiseSuppression/autoGainControl to getDisplayMedia audio,
 * which high-passes and processes it so shared music sounds thin/"tinny" — like
 * a microphone pickup rather than clean digital audio. The SDK calls
 * getDisplayMedia itself and exposes no hook, so wrap it to force clean stereo
 * capture. Call once at startup, before any screenshare.
 */
export function installCleanScreenshareAudio(): void {
  if (displayAudioPatched) return;
  const md = navigator.mediaDevices;
  if (!md || typeof md.getDisplayMedia !== 'function') return;
  displayAudioPatched = true;
  const orig = md.getDisplayMedia.bind(md);
  md.getDisplayMedia = (options?: DisplayMediaStreamOptions): Promise<MediaStream> => {
    const next: DisplayMediaStreamOptions = { ...options };
    if (next.audio) {
      const base = typeof next.audio === 'object' ? next.audio : {};
      // Keep suppressLocalAudioPlayback OFF (default) so the sharer still hears
      // the tab on their own speakers — muting local monitoring was confusing.
      next.audio = {
        ...base,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 2,
        sampleRate: 48_000, // pin Opus-native rate; avoid any capture resampling
      };
    }
    return orig(next);
  };
}

let broadcastSdpPatched = false;
let broadcastSdpActive = false;

/**
 * Opus encoder ceiling injected by the Broadcast SDP rewrite (512k = Opus max).
 * This is the *headroom* — the actual sent bitrate is governed live by the
 * producer's maxBitrate (the selectable Broadcast bitrate below), so the ceiling
 * can sit at max without forcing that rate.
 */
const OPUS_BROADCAST_BITRATE = 512_000;

/**
 * Toggle the Broadcast SDP rewrite. Call BEFORE (re)init so the next offer/
 * answer the new transport negotiates is (or isn't) rewritten. Gating keeps
 * Voice/Music on the SDK's stock (mono) negotiation.
 */
export function setBroadcastSdpActive(active: boolean): void {
  broadcastSdpActive = active;
}

function rewriteOpusForBroadcast(sdp: string): string {
  if (!sdp.includes('opus')) return sdp;
  const lines = sdp.split(/\r?\n/);
  const opusPts = new Set<string>();
  for (const l of lines) {
    const m = /^a=rtpmap:(\d+) opus/i.exec(l);
    if (m) opusPts.add(m[1]);
  }
  if (!opusPts.size) return sdp;
  return lines
    .map((l) => {
      const m = /^a=fmtp:(\d+) (.*)$/.exec(l);
      if (!m || !opusPts.has(m[1])) return l;
      const params = new Map<string, string | undefined>();
      for (const kv of m[2].split(';')) {
        const [k, v] = kv.split('=');
        if (k) params.set(k.trim(), v);
      }
      params.delete('usedtx'); // no DTX — avoids dropouts in shared music
      params.set('stereo', '1');
      params.set('sprop-stereo', '1');
      params.set('maxaveragebitrate', String(OPUS_BROADCAST_BITRATE));
      params.set('useinbandfec', '1');
      const joined = Array.from(params, ([k, v]) => (v === undefined ? k : `${k}=${v}`)).join(';');
      return `a=fmtp:${m[1]} ${joined}`;
    })
    .join('\r\n');
}

/**
 * Force TRUE stereo Opus on the local encoder for the Broadcast preset. Verified
 * live (headless Chrome + getStats): even with the SDK's supported
 * `enableStereo`, the RTCPeerConnection's negotiated SDP carries no `stereo=1`,
 * so libwebrtc downmixes the 2-channel tab/system audio to MONO. The SDK only
 * adds `stereo=1` to the offer it sends the SFU out-of-band (after
 * setLocalDescription), never to the encoder's own local/remote description.
 * Mirror installCleanScreenshareAudio: wrap setLocalDescription/
 * setRemoteDescription to inject `stereo=1;sprop-stereo=1;maxaveragebitrate` on
 * every Opus fmtp — but only while Broadcast is active (see setBroadcastSdpActive),
 * so Voice/Music keep stock negotiation. Call once at startup, before joining.
 */
export function installBroadcastAudioSdp(): void {
  if (broadcastSdpPatched) return;
  const PC = window.RTCPeerConnection;
  if (!PC) return;
  broadcastSdpPatched = true;
  type SetDesc = (d?: RTCSessionDescriptionInit) => Promise<void>;
  for (const method of ['setLocalDescription', 'setRemoteDescription'] as const) {
    const orig = PC.prototype[method] as unknown as SetDesc;
    const wrapped: SetDesc = function (this: RTCPeerConnection, desc?: RTCSessionDescriptionInit) {
      if (broadcastSdpActive && desc?.sdp) {
        desc = { type: desc.type, sdp: rewriteOpusForBroadcast(desc.sdp) };
      }
      return orig.call(this, desc);
    };
    (PC.prototype as unknown as Record<string, SetDesc>)[method] = wrapped;
  }
}

/** The mic audio producer (not the screenshare audio producer). */
function getMicAudioProducer(meeting: RTKClient) {
  const micTrackId = meeting.self.audioTrack?.id;
  const producers = meeting.self.producers;
  return (
    producers.find((p) => p.kind === 'audio' && p.track?.id === micTrackId) ??
    producers.find((p) => p.kind === 'audio')
  );
}

/**
 * Set the live Opus bitrate on the mic producer — verified in Phase 0 to move
 * the producer's targetBitrate without a rejoin. No-op if no producer yet.
 */
export async function setAudioBitrate(meeting: RTKClient, bps: number): Promise<void> {
  const producer = getMicAudioProducer(meeting);
  if (!producer) return;
  // priority/networkPriority protect audio's bitrate from video contention:
  // WebRTC's bandwidth allocator funds higher-priority encodings first, and
  // networkPriority marks the packets' DSCP. The SDK merges the whole params
  // object into the encoding, so these pass straight through.
  await producer.setRtpEncodingParameters({
    maxBitrate: bps,
    priority: 'high',
    networkPriority: 'high',
  });
}

function getScreenshareVideoProducer(meeting: RTKClient) {
  const ssTrackId = meeting.self.screenShareTracks?.video?.id;
  return meeting.self.producers.find((p) => p.kind === 'video' && p.track?.id === ssTrackId);
}

/** The screenshare/tab AUDIO producer (system audio when sharing with sound). */
function getScreenshareAudioProducer(meeting: RTKClient) {
  const ssAudioId = meeting.self.screenShareTracks?.audio?.id;
  if (!ssAudioId) return undefined;
  return meeting.self.producers.find((p) => p.kind === 'audio' && p.track?.id === ssAudioId);
}

/** Shared-audio Opus bitrate — at the Opus 510k max so tab/system music has full headroom. */
export const SCREENSHARE_AUDIO_BITRATE = 510_000;

/**
 * Boost the shared-audio (tab/system) producer's bitrate. The SDK exposes no
 * capture-constraint hook for screenshare audio and deprioritizes it behind
 * video, so a low default bitrate makes Opus narrow the band to ~speech. The
 * producer is created right after share start, so retry briefly until it's there.
 */
export async function setScreenshareAudioBitrate(meeting: RTKClient, bps: number): Promise<void> {
  for (let i = 0; i < 4; i++) {
    const producer = getScreenshareAudioProducer(meeting);
    if (producer) {
      try {
        // High priority so shared tab/system audio keeps its bitrate even while
        // the screenshare video competes for the same uplink (esp. 1080p/4K).
        await producer.setRtpEncodingParameters({
          maxBitrate: bps,
          priority: 'high',
          networkPriority: 'high',
        });
      } catch {
        // ignore — sharing without audio, or transient
      }
      return;
    }
    await new Promise((r) => setTimeout(r, 400));
  }
}

/**
 * Apply Detail/Motion to the active screenshare: contentHint on the live track,
 * capture constraints, and the screenshare producer's maxBitrate.
 */
export async function applyShareMode(meeting: RTKClient, mode: ShareMode): Promise<void> {
  const cfg = SHARE_MODE_CONFIG[mode];
  const videoTrack = meeting.self.screenShareTracks?.video;
  if (videoTrack) videoTrack.contentHint = cfg.contentHint;

  try {
    await meeting.self.updateScreenshareConstraints(cfg.constraints);
  } catch {
    // constraints may be unsupported by the captured surface — contentHint still applies
  }

  const producer = getScreenshareVideoProducer(meeting);
  if (producer) {
    try {
      // Low priority so the bandwidth allocator sacrifices screenshare VIDEO
      // before audio when the uplink is contended — audio fidelity wins.
      await producer.setRtpEncodingParameters({
        maxBitrate: cfg.maxBitrate,
        priority: 'low',
        networkPriority: 'low',
      });
    } catch {
      // ignore — contentHint/constraints already applied
    }
  }
}
